import React, { useEffect, useState } from 'react';
import { Typography } from '@duxton-web/ui';
import addFormats from 'ajv-formats';
import Ajv2019 from 'ajv/dist/2019';
import draft7MetaSchema from 'ajv/dist/refs/json-schema-draft-07.json';

import { JSONSchema } from '..';
import JsonSchemaConverterAlert from './JsonSchemaConverterAlert';
import { JSON_SCHEMA_DRAFT_07 } from '@/constants/jsonSchema';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import { variableJsonSchemaUpdate } from '@/features/variable/store/actions/variable/action-creators';
import JsonEditor from '@/pages/ExperimentFormV2/common/JsonEditor';
import { parse, prettifyJSON, prettifyJSONRaw } from '@/utils/json';

// Support for both draft-07 and draft-2019-09
// See: https://ajv.js.org/guide/schema-language.html#draft-07-and-draft-06
const ajv = new Ajv2019();
addFormats(ajv);
ajv.addMetaSchema(draft7MetaSchema); // Support draft-07 schemas in addition to draft-2019-09

interface JSONControlProps {
  value?: string;
  onChange?: (value: string | undefined, isValid: boolean) => void;
}

const JSONControl = ({
  value: valueProp,
  onChange: onChangeProp,
}: JSONControlProps) => {
  const { variable, disabled } = useVariableContext();
  const dispatchContext = useVariableDispatchContext();

  const externalValue =
    onChangeProp != null ? valueProp : variable?.metadata?.jsonSchema;

  const [error, setError] = useState<string>('');
  // Local state to preserve user input during typing (including invalid JSON)
  const [localValue, setLocalValue] = useState<string>(() =>
    prettifyJSONRaw(externalValue),
  );

  // Sync with external updates (e.g., from schema generator)
  useEffect(() => {
    setLocalValue(prettifyJSONRaw(externalValue));
  }, [externalValue]);

  const jsonSchemaCleanup = (value: { [key: string]: any }) => {
    if (value['$id'] === 'http://example.com/example.json') {
      delete value['$id'];
    }

    // Handle $schema versioning for compatibility
    // Backend supports: draft-04, draft-06, draft-07
    // Frontend (AJV 2019 with draft-07 meta-schema) supports: draft-07, draft-2019-09
    //
    // Strategy:
    // - Keep draft-2019-09 if present (for existing schemas, FE supports it)
    // - Keep draft-07 if present (compatible with both FE and BE)
    // - Default to draft-07 for new schemas (maximum compatibility)
    // - Convert incompatible versions (draft-2020-12, draft-04, draft-06) to draft-07
    const currentSchema = value['$schema'];
    const isCompatible =
      currentSchema &&
      (currentSchema.includes('draft-07') || currentSchema.includes('2019-09'));

    if (!currentSchema || !isCompatible) {
      // Missing or incompatible version - use draft-07 for maximum compatibility
      value['$schema'] = JSON_SCHEMA_DRAFT_07;
    }
    // else: keep existing compatible $schema (draft-07 or draft-2019-09)
  };

  const onJsonChange = (value: string) => {
    try {
      const parsedValue = parse(value);

      jsonSchemaCleanup(parsedValue);

      const validJSON = ajv.compile(parsedValue);

      if (!validJSON) {
        // Invalid schema: preserve user input, show error, disable Save
        setLocalValue(value);
        setError(ajv.errorsText());
        if (onChangeProp) {
          onChangeProp(undefined, false);
        } else {
          const lastValidSchema = variable?.metadata?.jsonSchema
            ? parse(variable.metadata.jsonSchema)
            : null;
          dispatchContext(variableJsonSchemaUpdate(lastValidSchema, false));
        }
        return;
      }

      // Valid schema: prettify and update both local state and Redux
      const prettified = prettifyJSON(parsedValue);
      setLocalValue(prettified);
      setError('');
      if (onChangeProp) {
        onChangeProp(prettified, true);
      } else {
        dispatchContext(
          variableJsonSchemaUpdate(parsedValue as JSONSchema, true),
        );
      }
    } catch (e) {
      // Parse error: preserve user input, show error, disable Save
      setLocalValue(value);
      if (!ajv.errors) setError('Invalid JSON Schema');
      else setError(ajv.errorsText());
      if (onChangeProp) {
        onChangeProp(undefined, false);
      } else {
        const lastValidSchema = variable?.metadata?.jsonSchema
          ? parse(variable.metadata.jsonSchema)
          : null;
        dispatchContext(variableJsonSchemaUpdate(lastValidSchema, false));
      }
    }
  };

  return (
    <>
      <JsonEditor
        readOnly={disabled}
        data={localValue}
        actionButtonIcon={null}
        onJsonChange={onJsonChange}
        height="330px"
        wrapEnabled
        debounceChangePeriod={700}
      />
      {error && (
        <Typography.Text data-testid="json-schema-error" type="danger">
          {error}
        </Typography.Text>
      )}
    </>
  );
};

interface JsonSchemaProps {
  value?: string;
  onChange?: (value: string | undefined, isValid: boolean) => void;
}

const JsonSchema = ({ value, onChange }: JsonSchemaProps = {}) => {
  const { previousVariable, variable, disabled } = useVariableContext();
  const dispatchContext = useVariableDispatchContext();

  const handleSchemaGenerated = (schema: string) => {
    const parsedSchema = parse(schema);

    if (onChange) {
      const prettified = prettifyJSON(parsedSchema);
      onChange(prettified, true);
    } else {
      dispatchContext(
        variableJsonSchemaUpdate(parsedSchema as JSONSchema, true),
      );
    }
  };

  if (previousVariable) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Typography.Text strong>JSON Schema</Typography.Text>
        <JsonSchemaConverterAlert
          onSchemaGenerated={handleSchemaGenerated}
          disabled={disabled}
          schemaTitle={`Generated schema for ${variable.name || 'Variable'}`}
        />
      </div>

      <JSONControl value={value} onChange={onChange} />
    </div>
  );
};

export default JsonSchema;
